// src/screens/UpdateInvoiceItem.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Styling/InvoiceCard.css";
import "../Styling/Fields.css";
import { useAuth } from "../Context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Base_URL } from "../url";
import axios from "axios";

// helpers copied from CreateInvoiceItem file (already present in your repo)
import {
  fetchBatchByItem,
  fetchBatchPrice,
  fetchItemsByOrgId,
  fetchgstdetails,
} from "../Api/IncomingPo";

const UpdateInvoiceItem = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { user } = useAuth();
  const userId = localStorage.getItem("userId") || "5";

  const getInvoiceIdFromQuery = () => {
    if (invoiceId) return invoiceId;
    const params = new URLSearchParams(window.location.search);
    return params.get("id") || "";
  };
  const effectiveInvoiceId = getInvoiceIdFromQuery();

  const [items, setItems] = useState([]); // list to allow dropdown later
  const [hsnCodes, setHsnCodes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [showHsn, setShowHsn] = useState({}); // keep shape compatible
  const [loading, setLoading] = useState(false);
  const [savingItems, setSavingItems] = useState(false);
  const [batchItemsByIndex, setBatchItemsByIndex] = useState({}); // { index: [batches] }

  const [invoiceData, setInvoiceData] = useState({
    transactionType: "Invoice",
    companyCode: "",
    category: "",
    customerCode: "",
    projectCode: "",
    poNumber: "",
    poDate: "",
    salespersonCode: "",
    createdBy: userId,
    itemData: [
      {
        item: "",
        resource: "",
        vendor: "",
        quantity: "",
        price: "",
        taxType: "",
        cgst: "0",
        sgst: "0",
        igst: "0",
        hsn: "",
        grandTotal: "",
        batchId: "",
        UOM: "",
        discountPercent: "0",
        discountValue: "",
        taxableValue: 0,
        itemId: null,
        itemName: "",
        batchLabel: "",
      },
    ],
  });

  // fetch organisation items once (used to populate the new-row dropdown)
  useEffect(() => {
    const loadItemsForOrg = async () => {
      try {
        if (!user?.organisationId) return;
        const list = await fetchItemsByOrgId(user.organisationId);
        if (Array.isArray(list)) setItems(list);
      } catch (e) {
        console.warn("Failed to fetch items by org", e);
      }
    };
    loadItemsForOrg();
  }, [user]);

  // Price summary calculation (unchanged)
  const calculatePriceSummary = () => {
  let totalBasePrice = 0;
  let totalTaxAmount = 0;
  let totalPrice = 0;

  invoiceData.itemData.forEach((item) => {
    const quantity = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;

    // prefer taxableValue if backend provided it; otherwise compute from qty*price-discount
    let base = quantity * price;
    if (item.taxableValue && !isNaN(Number(item.taxableValue)) && Number(item.taxableValue) > 0) {
      base = Number(item.taxableValue);
    }

    const cgst = Number(item.cgst) || 0;
    const sgst = Number(item.sgst) || 0;
    const igst = Number(item.igst) || 0;

    const taxAmount = base * ((cgst + sgst + igst) / 100);

    totalBasePrice += base;
    totalTaxAmount += taxAmount;
    totalPrice += base + taxAmount;
  });

  // compute roundoff vs roundedTotalPrice if you want same behavior as before
  const roundedTotalPrice = totalPrice;
  const roundOff = roundedTotalPrice - totalPrice;

  return {
    totalBasePrice: totalBasePrice.toFixed(2),
    totalTaxAmount: totalTaxAmount.toFixed(2),
    totalPrice: totalPrice.toFixed(2),
    roundedTotalPrice: roundedTotalPrice.toFixed(2),
    roundOff: roundOff.toFixed(2),
  };
};


  const priceSummary = calculatePriceSummary();

  // Fetch update invoice items and preload batches for each row
  useEffect(() => {
    const fetchUpdateInvoice = async () => {
      if (!effectiveInvoiceId) {
        toast.warning("No invoice id provided in route or query (?id=...)");
        return;
      }

      setLoading(true);
      try {
        const url = `${Base_URL}/Invoices/GetUpdateInvoice/id?id=${encodeURIComponent(
          effectiveInvoiceId
        )}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch (${res.status})`);
        const data = await res.json();
        console.log("raw update invoice response:", data);

        if (!Array.isArray(data) || data.length === 0) {
          setLoading(false);
          return;
        }

        const mappedItems = data.map((entry) => {
          const inv = entry?.invoiceItems ?? {};
          const itemObj = entry?.item ?? {};

          const quantity = inv.quantity ?? "";
          const price = inv.price ?? itemObj.price ?? "";
          const itemCode = inv.item ?? itemObj.itemCode ?? "";
          const uom = inv.unitCode ?? itemObj.skuUnit ?? "";
          const cgst = inv.cgst ?? "0";
          const sgst = inv.sgst ?? "0";
          const igst = inv.igst ?? "0";
          const discPercent =
            inv.discPercent !== undefined && inv.discPercent !== null
              ? String(inv.discPercent)
              : "0";

          const grandTotal =
            inv.grandTotal ??
            (inv.grossValue !== undefined && inv.grossValue !== null
              ? String(inv.grossValue)
              : "");

          return {
            item: itemCode,
            resource: inv.resource ?? "",
            vendor: inv.vendor ?? "",
            quantity,
            price,
            taxType: inv.taxType ?? "",
            cgst: String(cgst),
            sgst: String(sgst),
            igst: String(igst),
            hsn: inv.hsn ?? itemObj.hsnId ?? "",
            grandTotal: grandTotal ?? "",
            batchId: inv.batchId ?? "",
            UOM: uom,
            discountPercent: discPercent,
            discountValue:
              inv.discountValue !== undefined ? String(inv.discountValue) : "",
            taxableValue:
              inv.taxableValue !== undefined ? Number(inv.taxableValue) : 0,
            scgstAmount: inv.cgstAmount ?? null,
            igstAmount: inv.igstAmount ?? null,
            itemId: inv.itemId ?? null,
            itemName: itemObj.itemName ?? "",
            itemCodeId: itemObj.itemCodeId ?? "",
            batchLabel: "", // will be set after batch load if batch exists
          };
        });

        // set items first to render rows
        setInvoiceData((prev) => ({
          ...prev,
          itemData: mappedItems.length > 0 ? mappedItems : prev.itemData,
        }));

        // now preload batches for each row and set batch label/price if available
        mappedItems.forEach(async (row, idx) => {
          // Preload batches/prices/gst for all rows and compute final rows before final set
try {
  const enrichedItems = await Promise.all(
    mappedItems.map(async (row) => {
      // shallow copy to mutate
      const r = { ...row };

      if (!r.item) return r;

      // fetch batches for the item
      let batches = [];
      try {
        batches = await fetchBatchByItem(r.item);
      } catch (e) {
        console.warn("fetchBatchByItem failed for", r.item, e);
      }

      // store batch list on the row for mapping later
      r._batches = batches || [];

      // If there's a pre-selected batchId, try to match and fetch price
      if (r.batchId && Array.isArray(batches) && batches.length > 0) {
        const selected = batches.find(
          (b) =>
            String(b.batchId) === String(r.batchId) ||
            b.batchNoLabel === r.batchId
        );
        if (selected) {
          r.batchLabel = selected.batchNoLabel ?? "";
          try {
            const priceResp = await fetchBatchPrice(selected.batchId);
            const apiPrice = priceResp?.ptrExclGst ?? priceResp?.price ?? null;
            if (apiPrice !== undefined && apiPrice !== null && apiPrice !== "") {
              r.price = String(apiPrice);
            }
          } catch (e) {
            console.warn("fetchBatchPrice failed", e);
          }
        }
      }

      // If HSN present and gst missing, fetch GST details
      if ((r.cgst === "" || r.sgst === "" || r.igst === "") && r.hsn) {
        try {
          const gstdetails = await fetchgstdetails(effectiveInvoiceId, r.hsn);
          if (gstdetails) {
            r.cgst = String(gstdetails.cgstPercent ?? r.cgst ?? "0");
            r.sgst = String(gstdetails.sgstPercent ?? r.sgst ?? "0");
            r.igst = String(gstdetails.igstPercent ?? r.igst ?? "0");
          }
        } catch (e) {
          console.warn("fetchgstdetails failed", e);
        }
      }

      // Now recompute totals for the row consistently here
      const qty = parseFloat(r.quantity) || 0;
      const p = parseFloat(r.price) || 0;
      const subtotal = qty * p;
      const discountPercentNum = parseFloat(r.discountPercent) || 0;
      const discountValueNum = subtotal * (discountPercentNum / 100);
      const taxableAmount = subtotal - discountValueNum;
      const cgstPercent = parseFloat(r.cgst) || 0;
      const sgstPercent = parseFloat(r.sgst) || 0;
      const igstPercent = parseFloat(r.igst) || 0;
      const taxAmount = taxableAmount * ((cgstPercent + sgstPercent + igstPercent) / 100);
      const grandTotalNum = taxableAmount + taxAmount;

      r.discountValue = discountValueNum.toFixed(2);
      r.grandTotal = grandTotalNum.toFixed(2).toString();
      r.taxableValue = Number(taxableAmount.toFixed(2));

      return r;
    })
  );

  // Now set invoiceData and also set batchItemsByIndex mapping from enrichedItems
  setInvoiceData((prev) => ({ ...prev, itemData: enrichedItems }));

  const mapping = {};
  enrichedItems.forEach((it, idx) => {
    mapping[idx] = it._batches || [];
    // cleanup temporary field if you don't want it inside itemData
    delete it._batches;
  });
  setBatchItemsByIndex(mapping);
} catch (err) {
  console.warn("Error enriching items:", err);
}

        });
      } catch (err) {
        console.error("Failed loading update invoice items:", err);
        toast.error("Failed to load invoice items for update");
      } finally {
        setLoading(false);
      }
    };

    fetchUpdateInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveInvoiceId]);

  // header change handler (kept for parity)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData((prev) => ({ ...prev, [name]: value }));
  };

  // handle per-item changes and recompute totals; also handle batchId change to fetch price
  const handleItemChange = async (index, e) => {
    const { name, value } = e.target;

    const numericFields = [
      "quantity",
      "price",
      "cgst",
      "sgst",
      "igst",
      "grandTotal",
      "discountPercent",
    ];
    const cleanedValue = numericFields.includes(name)
      ? value.replace(/[^0-9.]/g, "")
      : value;

    const recomputeRow = (row) => {
      const quantityNum = parseFloat(row.quantity) || 0;
      const priceNum = parseFloat(row.price) || 0;
      const discountPercentNum = parseFloat(row.discountPercent) || 0;

      const subtotal = quantityNum * priceNum;
      const discountValueNum = subtotal * (discountPercentNum / 100);

      const taxableAmount = subtotal - discountValueNum;

      const cgstPercent = parseFloat(row.cgst) || 0;
      const sgstPercent = parseFloat(row.sgst) || 0;
      const igstPercent = parseFloat(row.igst) || 0;

      const taxAmount =
        taxableAmount * ((cgstPercent + sgstPercent + igstPercent) / 100);

      const grandTotalNum = taxableAmount + taxAmount;

      return {
        ...row,
        discountValue: discountValueNum.toFixed(2),
        grandTotal: grandTotalNum.toFixed(2).toString(),
        taxableValue: Number(taxableAmount.toFixed(2)),
      };
    };

    // If user selected an item from the dropdown for a NEW row
    if (name === "item") {
      const selectedItemCode = value;
      // lookup item object from preloaded items list
      const selectedItemObj = items.find(
        (it) => it.itemCode === selectedItemCode || it.itemCodeId === selectedItemCode
      );

      // update the row with item meta
      setInvoiceData((prev) => {
        const itemData = [...prev.itemData];
        const row = { ...(itemData[index] ?? {}) };
        row.item = selectedItemCode;
        row.itemName = selectedItemObj?.itemName ?? "";
        row.UOM = selectedItemObj?.skuUnit ?? row.UOM;
        // prefer item object price if provided
        if (selectedItemObj?.price) row.price = String(selectedItemObj.price);
        // set hsn if available on item metadata
        if (selectedItemObj?.hsnId) row.hsn = selectedItemObj.hsnId;
        itemData[index] = recomputeRow(row);
        return { ...prev, itemData };
      });

      // fetch batches for the new item and set them for the row
      try {
        const batches = await fetchBatchByItem(selectedItemCode);
        setBatchItemsByIndex((prev) => ({ ...prev, [index]: batches || [] }));

        // if batches exist, optionally pick first batch and fetch its price
        if (batches && batches.length > 0) {
          const chosen = batches[0];
          const priceResp = await fetchBatchPrice(chosen.batchId);
          const apiPrice = priceResp?.ptrExclGst ?? priceResp?.price ?? null;

          setInvoiceData((prev) => {
            const itemData = [...prev.itemData];
            const row = { ...(itemData[index] ?? {}) };
            row.batchId = chosen.batchId;
            row.batchLabel = chosen.batchNoLabel ?? "";
            if (apiPrice !== undefined && apiPrice !== null && apiPrice !== "") {
              row.price = String(apiPrice);
            }
            itemData[index] = recomputeRow(row);
            return { ...prev, itemData };
          });
        }

        // fetch GST details by HSN if available
        const hsnIdToUse = selectedItemObj?.hsnId ?? null;
        if (hsnIdToUse) {
          try {
            const gstdetails = await fetchgstdetails(effectiveInvoiceId, hsnIdToUse);
            if (gstdetails) {
              setInvoiceData((prev) => {
                const itemData = [...prev.itemData];
                const row = { ...(itemData[index] ?? {}) };
                row.cgst = String(gstdetails.cgstPercent ?? row.cgst ?? "0");
                row.sgst = String(gstdetails.sgstPercent ?? row.sgst ?? "0");
                row.igst = String(gstdetails.igstPercent ?? row.igst ?? "0");
                itemData[index] = recomputeRow(row);
                return { ...prev, itemData };
              });
            }
          } catch (e) {
            console.warn("failed to fetch gst details", e);
          }
        }
      } catch (e) {
        console.warn("failed to fetch batches for selected item", e);
      }

      return;
    }

    // immediate update for basic fields
    setInvoiceData((prev) => {
      const itemData = [...prev.itemData];
      const existing = itemData[index] ?? {};
      const updated = { ...existing, [name]: cleanedValue };
      const shouldRecalc = [
        "quantity",
        "price",
        "discountPercent",
        "cgst",
        "sgst",
        "igst",
      ].includes(name);
      itemData[index] = shouldRecalc ? recomputeRow(updated) : updated;
      return { ...prev, itemData };
    });

    // if batch changed, fetch batch price + label and update row
    if (name === "batchId") {
      const selectedBatchId = cleanedValue;
      try {
        const batchesForRow = batchItemsByIndex[index] || [];
        const selectedBatchObj = batchesForRow.find(
          (b) =>
            String(b.batchId) === String(selectedBatchId) ||
            b.batchNoLabel === selectedBatchId
        );

        if (selectedBatchObj) {
          const priceResp = await fetchBatchPrice(selectedBatchObj.batchId);
          const apiPrice = priceResp?.ptrExclGst ?? priceResp?.price ?? null;

          setInvoiceData((prev) => {
            const itemData = [...prev.itemData];
            const row = { ...(itemData[index] ?? {}) };
            row.batchId = String(selectedBatchObj.batchId);
            row.batchLabel = String(selectedBatchObj.batchNoLabel ?? "");
            if (apiPrice !== undefined && apiPrice !== null && apiPrice !== "") {
              row.price = String(apiPrice);
            }
            // recompute totals after price change
            const recomputed = (() => {
              const q = parseFloat(row.quantity) || 0;
              const p = parseFloat(row.price) || 0;
              const subtotal = q * p;
              const discountPercentNum = parseFloat(row.discountPercent) || 0;
              const discountValueNum = subtotal * (discountPercentNum / 100);
              const taxableAmount = subtotal - discountValueNum;
              const cgstPercent = parseFloat(row.cgst) || 0;
              const sgstPercent = parseFloat(row.sgst) || 0;
              const igstPercent = parseFloat(row.igst) || 0;
              const taxAmount =
                taxableAmount * ((cgstPercent + sgstPercent + igstPercent) / 100);
              const grandTotalNum = taxableAmount + taxAmount;
              row.discountValue = discountValueNum.toFixed(2);
              row.grandTotal = grandTotalNum.toFixed(2).toString();
              row.taxableValue = Number(taxableAmount.toFixed(2));
              return row;
            })();
            itemData[index] = recomputed;
            return { ...prev, itemData };
          });
        } else {
          // set batchId even if not found in list
          setInvoiceData((prev) => {
            const itemData = [...prev.itemData];
            const row = { ...(itemData[index] ?? {}) };
            row.batchId = String(selectedBatchId);
            itemData[index] = row;
            return { ...prev, itemData };
          });
        }
      } catch (e) {
        console.warn("failed to handle batch change", e);
      }
    }
  };

  const addNewItem = () => {
    setInvoiceData((prev) => ({
      ...prev,
      itemData: [
        ...prev.itemData,
        {
          item: "",
          resource: "",
          vendor: "",
          quantity: "",
          price: "",
          taxType: "",
          cgst: "0",
          sgst: "0",
          igst: "0",
          hsn: "",
          grandTotal: "",
          batchId: "",
          UOM: "",
          discountPercent: "0",
          discountValue: "",
          taxableValue: 0,
          itemId: null,
          itemName: "",
          batchLabel: "",
        },
      ],
    }));
  };

  const removeItem = (index) => {
    if (invoiceData.itemData.length === 1) {
      toast.warning("At least one item is required");
      return;
    }
    const itemData = [...invoiceData.itemData];
    itemData.splice(index, 1);
    setInvoiceData({ ...invoiceData, itemData });
    // also remove batches for that index
    setBatchItemsByIndex((prev) => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  // Save handler: prepare payload and post
  const handleSaveItems = async () => {
    setSavingItems(true);
    try {
      const payload = {
        invoiceId: effectiveInvoiceId,
        updatedBy: userId,
        invoiceItem: invoiceData.itemData.map((it) => {
          return {
            itemId: it.itemId,
            item: it.item,
            quantity: it.quantity,
            price: it.price,
            taxType: it.taxType,
            cgst: it.cgst,
            sgst: it.sgst,
            igst: it.igst,
            hsn: it.hsn,
            batchId: it.batchId,
            UOM: it.UOM,
            discPercent: it.discountPercent,
            discountValue: it.discountValue,
            taxableValue: it.taxableValue,
            grandTotal: it.grandTotal,
            vendor: it.vendor,
            resource: it.resource,
          };
        }),
      };
      const res = await axios.post(
        `${Base_URL}/Invoices/UpdateInvoiceItem/id?id=${payload.invoiceId}`,
        payload
      );

      console.log("UPDATE INVOICE PAYLOAD:", payload);
      if (res.data) {
        toast.success(`${payload.invoiceId} Updated successfully`);
      }
    } catch (err) {
      console.error("Error preparing payload:", err);
      toast.error("Failed to prepare payload");
    } finally {
      setSavingItems(false);
    }
  };

  const isLoading = loading;

  return (
    <div className="container-fluid py-4">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div className="card shadow-sm border-0 rounded-4 p-4 invoice-card mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold text-dark mb-1">
              Update Invoice Items
              <span className="badge bg-success-light text-success ms-2">
                Invoice ID: {effectiveInvoiceId || "—"}
              </span>
            </h4>
            <p className="text-muted mb-2">Edit items and save updates</p>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary rounded-3 fw-semibold"
              onClick={()=>navigate(`/updateShipping/${invoiceId}`)}
              disabled={savingItems || isLoading}
            >
              Update Shipping
            </button>
            <button
              className="btn btn-secondary rounded-3 fw-semibold"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="fields-form">
        <div className="d-flex align-items-center mb-4">
          <div className="icon-box me-2">📦</div>
          <h5 className="fw-bold mb-0 text-dark fields-title">Invoice Items</h5>
        </div>

        {/* Price Summary */}
        <div className="card shadow-sm border-0 rounded-4 p-4 mt-4">
          <div className="d-flex align-items-center mb-3">
            <div className="icon-box me-2">💰</div>
            <h6 className="fw-bold mb-0 text-dark">Price Summary</h6>
          </div>

          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Total Base Price</label>
              <input
                type="text"
                className="form-control"
                value={priceSummary.totalBasePrice}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Total Tax Amount</label>
              <input
                type="text"
                className="form-control"
                value={priceSummary.totalTaxAmount}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Round off</label>
              <input
                type="text"
                className="form-control"
                value={priceSummary.roundOff}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Total Amount</label>
              <input
                type="text"
                className="form-control"
                value={priceSummary.roundedTotalPrice}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Item rows */}
        <div className="item-data-section mt-4">
          <div className="d-flex align-items-center mb-3">
            <div className="icon-box me-2">📦</div>
            <h6 className="fw-bold mb-0 text-dark item-data-heading">Item Data</h6>
          </div>

          {invoiceData.itemData.map((item, index) => (
            <div key={index} className="item-data-container">
              <div className="item-data-row">
                {/* If existing preloaded row (has itemId) -> show readOnly code/name */}
                {item.itemId ? (
                  <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                    <div className="item-data-field" style={{ width: "180px" }}>
                      <label className="form-label fw-semibold">
                        Item Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="item"
                        value={item.item || ""}
                        readOnly
                      />
                    </div>

                    <div className="item-data-field" style={{ width: "320px" }}>
                      <label className="form-label fw-semibold">Item Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="itemName"
                        value={item.itemName || ""}
                        readOnly
                      />
                    </div>
                  </div>
                ) : (
                  // New row -> show select dropdown for item (code - name)
                  <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                    <div className="item-data-field" style={{ width: "260px" }}>
                      <label className="form-label fw-semibold">
                        Item <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="item"
                        value={item.item || ""}
                        onChange={(e) => handleItemChange(index, e)}
                      >
                        <option value="">Select Item</option>
                        {items.map((it) => (
                          <option key={it.itemCode} value={it.itemCode}>
                            {it.itemCode} - {it.itemName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="item-data-field" style={{ width: "240px" }}>
                      <label className="form-label fw-semibold">Item Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          item.itemName ||
                          (items.find((it) => it.itemCode === item.item)?.itemName ?? "")
                        }
                        readOnly
                      />
                    </div>
                  </div>
                )}

                {/* Batch dropdown (preloaded) */}
                <div className="item-data-field" style={{ width: "140px", marginLeft: "12px" }}>
                  <label className="form-label fw-semibold">Batch</label>
                  <select
                    className="form-control"
                    name="batchId"
                    value={item.batchId || ""}
                    onChange={(e) => handleItemChange(index, e)}
                  >
                    <option value="">Select Batch</option>
                    {(batchItemsByIndex[index] || []).map((b) => (
                      <option key={b.batchId} value={b.batchId}>
                        {b.batchNoLabel}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vendor (optional) */}
                {showHsn.vendorOnInvLine === "Y" && (
                  <div className="item-data-field" style={{ width: "180px" }}>
                    <label className="form-label fw-semibold">Vendor</label>
                    <select className="form-select" name="vendor" value={item.vendor} onChange={(e) => handleItemChange(index, e)}>
                      <option value="">Select Vendor</option>
                      {vendors.map((v) => (
                        <option key={v.vendorCode} value={v.vendorCode}>
                          {v.vendorCode} - {v.vendorName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Qty */}
                <div className="item-data-field" style={{ width: "120px" }}>
                  <label className="form-label fw-semibold">
                    Qty <span className="text-danger">*</span>
                  </label>
                  <input type="text" className="form-control" name="quantity" value={item.quantity} onChange={(e) => handleItemChange(index, e)} />
                </div>

                {/* Price */}
                <div className="item-data-field" style={{ width: "120px" }}>
                  <label className="form-label fw-semibold">
                    Price <span className="text-danger">*</span>
                  </label>
                  <input type="text" className="form-control" name="price" value={item.price} onChange={(e) => handleItemChange(index, e)} />
                </div>

                {/* HSN */}
                {showHsn.hsnOnInvLine === "Y" && (
                  <div className="item-data-field" style={{ width: "150px" }}>
                    <label className="form-label fw-semibold">HSN <span className="text-danger">*</span></label>
                    <select className="form-select" name="hsn" value={item.hsn} onChange={(e) => handleItemChange(index, e)}>
                      <option value="">Select HSN</option>
                      {hsnCodes.map((h) => (
                        <option key={h.code} value={h.code}>
                          {h.code} - {h.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Discount %, Discount value, Grand total */}
                <div className="item-data-field" style={{ width: "120px" }}>
                  <label className="form-label fw-semibold">Discount%</label>
                  <input type="number" className="form-control" name="discountPercent" value={item.discountPercent} onChange={(e) => handleItemChange(index, e)} />
                </div>
                <div className="item-data-field" style={{ width: "120px" }}>
                  <label className="form-label fw-semibold">Discount Value</label>
                  <input type="text" className="form-control" name="discountValue" value={item.discountValue} readOnly />
                </div>
                <div className="item-data-field" style={{ width: "120px" }}>
                  <label className="form-label fw-semibold">Grand Total</label>
                  <input type="text" className="form-control" name="grandTotal" value={item.grandTotal} readOnly />
                </div>

                {/* Actions */}
                <div className="item-data-field" style={{ width: "200px", paddingTop: "25px" }}>
                  <button type="button" className="btn btn-danger rounded-3 fw-semibold item-action-btn me-2" onClick={() => removeItem(index)} disabled={invoiceData.itemData.length === 1}>
                    Remove
                  </button>
                  {index === invoiceData.itemData.length - 1 && (
                    <button type="button" className="btn btn-success rounded-3 fw-semibold item-action-btn" onClick={addNewItem}>
                      Add Item
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Save */}
          <div className="fields-btn-container mt-4">
            <button type="button" className="btn btn-primary rounded-3 fw-semibold px-4 fields-submit" onClick={handleSaveItems} disabled={savingItems || isLoading}>
              {savingItems ? "Saving Items..." : "💾 Save Invoice Updates"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateInvoiceItem;
